export const CARD_WIDTH = 212
export const CARD_HEIGHT = 68
export const CARD_GUTTER = 10
export const ROW_HEIGHT = 80
export const LEADER_GUTTER = 24

/** Cluster prefix for synthetic ids. Picked so it can't collide with the
 *  backend's deterministic timeline-item ids (`{label}:{element_id}:{kind}`). */
export const CLUSTER_ID_PREFIX = '__cluster__:'

export type Lane = 'above' | 'below'

/** Discriminated union: either a single event takes a layout slot, or a
 *  cluster does. A cluster only appears when ≥ 2 input events share the
 *  EXACT same timestamp string. Strict equality is intentional — the user
 *  ruled out proximity-based clustering (no "nearby" merging) because they
 *  want every distinct second to remain individually inspectable. */
export type LayoutEntry =
  | {
      kind: 'event'
      id: string
      lane: Lane
      row: number
      x: number
    }
  | {
      kind: 'cluster'
      /** Synthetic id of the form ``CLUSTER_ID_PREFIX + timestamp``. */
      id: string
      lane: Lane
      row: number
      x: number
      /** Member event ids, in input order. */
      memberIds: string[]
      /** Shared timestamp (for the badge label). */
      timestamp: string
    }

export interface LayoutInput {
  id: string
  timestamp: string
}

/** Helper for callers that only care about the geometry (x/lane/row). */
export function layoutEntryX(entry: LayoutEntry): number {
  return entry.x
}

/**
 * Greedy lane + row assignment with strict same-timestamp clustering.
 *
 * Pipeline:
 *  1. Group input events by exact timestamp string. Groups of size ≥ 2
 *     become a single cluster entry; groups of size 1 stay as events.
 *  2. Sort by timestamp.
 *  3. Each entry gets a preferred lane (alternating above/below by index).
 *  4. Within the chosen lane, pick the lowest row where the card footprint
 *     (x ± CARD_WIDTH/2 + gutter) does not intersect an already-placed
 *     footprint.
 *  5. If both lanes already have > MAX_ROWS_BEFORE_SWAP rows and only the
 *     opposite lane would land lower, swap sides.
 *
 * Result keys: ``event.id`` for events, ``CLUSTER_ID_PREFIX + ts`` for
 * clusters. Callers iterate ``result.values()`` to render — events not in
 * the map have been absorbed into a cluster.
 */
export function layoutEvents<T extends LayoutInput>(
  events: readonly T[],
  xOf: (ts: string) => number
): Map<string, LayoutEntry> {
  // 1. Cluster pre-pass: bucket by EXACT timestamp string (no rounding,
  // no proximity). Stable iteration order matters so the result is
  // deterministic.
  const buckets = new Map<string, T[]>()
  for (const ev of events) {
    const arr = buckets.get(ev.timestamp)
    if (arr) arr.push(ev)
    else buckets.set(ev.timestamp, [ev])
  }

  // 2. Materialise placement candidates.
  type Placement = { id: string; timestamp: string; isCluster: boolean; memberIds: string[] }
  const placements: Placement[] = []
  for (const [ts, members] of buckets) {
    if (members.length >= 2) {
      placements.push({
        id: `${CLUSTER_ID_PREFIX}${ts}`,
        timestamp: ts,
        isCluster: true,
        memberIds: members.map(m => m.id),
      })
    } else {
      placements.push({
        id: members[0].id,
        timestamp: ts,
        isCluster: false,
        memberIds: [members[0].id],
      })
    }
  }

  // 3-5. Lane / row assignment (unchanged from the pre-clustering version
  // beyond operating on placements rather than raw events).
  const aboveRows: Array<Array<[number, number]>> = []
  const belowRows: Array<Array<[number, number]>> = []
  const result = new Map<string, LayoutEntry>()
  const MAX_ROWS_BEFORE_SWAP = 2

  function firstFreeRow(
    rows: Array<Array<[number, number]>>,
    xStart: number,
    xEnd: number
  ): number {
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i]
      let collides = false
      for (const [a, b] of row) {
        if (xEnd >= a && xStart <= b) {
          collides = true
          break
        }
      }
      if (!collides) return i
    }
    return rows.length
  }

  function place(
    rows: Array<Array<[number, number]>>,
    rowIdx: number,
    xStart: number,
    xEnd: number
  ) {
    while (rows.length <= rowIdx) rows.push([])
    rows[rowIdx].push([xStart, xEnd])
  }

  placements
    .slice()
    .sort((a, b) => (a.timestamp < b.timestamp ? -1 : a.timestamp > b.timestamp ? 1 : 0))
    .forEach((p, idx) => {
      const x = xOf(p.timestamp)
      const xStart = x - CARD_WIDTH / 2 - CARD_GUTTER / 2
      const xEnd = x + CARD_WIDTH / 2 + CARD_GUTTER / 2

      const preferredAbove = idx % 2 === 0
      const preferred = preferredAbove ? aboveRows : belowRows
      const alternate = preferredAbove ? belowRows : aboveRows

      const preferredRow = firstFreeRow(preferred, xStart, xEnd)
      let lane: Lane = preferredAbove ? 'above' : 'below'
      let row = preferredRow

      if (preferredRow >= MAX_ROWS_BEFORE_SWAP) {
        const alternateRow = firstFreeRow(alternate, xStart, xEnd)
        if (alternateRow < preferredRow) {
          lane = preferredAbove ? 'below' : 'above'
          row = alternateRow
        }
      }

      place(lane === 'above' ? aboveRows : belowRows, row, xStart, xEnd)
      result.set(
        p.id,
        p.isCluster
          ? {
              kind: 'cluster',
              id: p.id,
              lane,
              row,
              x,
              memberIds: p.memberIds,
              timestamp: p.timestamp,
            }
          : { kind: 'event', id: p.id, lane, row, x }
      )
    })

  return result
}

/** Y-coordinate of the card's top edge. */
export function cardTopY(axisY: number, entry: LayoutEntry): number {
  if (entry.lane === 'above') {
    return axisY - LEADER_GUTTER - (entry.row + 1) * ROW_HEIGHT
  }
  return axisY + LEADER_GUTTER + entry.row * ROW_HEIGHT
}

/** Y-coordinate of the end of the leader line attached to the card side facing the axis. */
export function leaderCardY(axisY: number, entry: LayoutEntry): number {
  const top = cardTopY(axisY, entry)
  return entry.lane === 'above' ? top + ROW_HEIGHT - 6 : top + 6
}
